import {
  ActionIcon,
  AppShell,
  Badge,
  Burger,
  Button,
  Divider,
  Group,
  Loader,
  NavLink,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconActivity,
  IconBook,
  IconCoin,
  IconAdjustments,
  IconGavel,
  IconDatabase,
  IconLogout,
  IconMail,
  IconMoon,
  IconSparkles,
  IconSun,
  IconTags,
  IconTemplate,
  IconTexture,
  IconRocket,
  IconRoute,
  IconSitemap,
  IconUsers,
  IconWand,
} from '@tabler/icons-react';
import { useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { LanguageSwitcher } from './components/LanguageSwitcher';
import { useLlmInfo } from './hooks/use-content';
import { useT, type MessageKey } from './i18n';
import { signInWithEmail, signInWithGoogle, signOut, useSession } from './lib/auth';
import { BankHealthScreen } from './screens/BankHealthScreen';
import { BusinessRulesScreen } from './screens/BusinessRulesScreen';
import { ConfigScreen } from './screens/ConfigScreen';
import { ContentScreen } from './screens/ContentScreen';
import { CostsScreen } from './screens/CostsScreen';
import { DatabaseScreen } from './screens/DatabaseScreen';
import { EmailScreen } from './screens/EmailScreen';
import { GenerateScreen } from './screens/GenerateScreen';
import { GuideScreen } from './screens/GuideScreen';
import { HealthScreen } from './screens/HealthScreen';
import { ItemModelsScreen } from './screens/ItemModelsScreen';
import { LaunchReadinessScreen } from './screens/LaunchReadinessScreen';
import { QuestionBankScreen } from './screens/QuestionBankScreen';
import { QuestionFlowScreen } from './screens/QuestionFlowScreen';
import { ReviewQueueScreen } from './screens/ReviewQueueScreen';
import { ReviewScreen } from './screens/ReviewScreen';
import { RoadmapsScreen } from './screens/RoadmapsScreen';
import { SimulateScreen } from './screens/SimulateScreen';
import { SkillQueueScreen } from './screens/SkillQueueScreen';
import { SkillsScreen } from './screens/SkillsScreen';
import { TraceabilityScreen } from './screens/TraceabilityScreen';
import { UserFlowScreen } from './screens/UserFlowScreen';
import { UsersScreen } from './screens/UsersScreen';

type Tab = { path: string; labelKey: MessageKey; icon: typeof IconWand };

/**
 * Each tab is a route: clicking it changes the URL, and deep links restore it.
 * Grouped by what the operator is actually doing, so the three screens that read
 * the same product document (business rules → blueprint → RTM) sit together
 * under one heading instead of being lost among the platform tools.
 *
 * Labels are message KEYS, not text. The table is module-level and so cannot
 * call a hook; resolving each label at render is also what makes a language
 * switch repaint the nav without a reload.
 */
const NAV_SECTIONS: { titleKey: MessageKey; items: Tab[] }[] = [
  {
    titleKey: 'app.nav.authoring',
    items: [
      // PRE-284: generating and browsing were one screen and are two jobs —
      // one decides once and waits, the other narrows until something appears.
      { path: '/generate', labelKey: 'screen.generate', icon: IconWand },
      { path: '/questions', labelKey: 'screen.questions', icon: IconTemplate },
      // PRE-293: where the expert writes the shape of a question rather than the
      // question. Next to Generate because they are the two ways to make a
      // draft, and the difference between them is what is being decided.
      { path: '/item-models', labelKey: 'screen.itemModels', icon: IconWand },
      { path: '/skills', labelKey: 'screen.skills', icon: IconTags },
      // BR-90 / PRE-123: the skills CVs named that we had no row for.
      { path: '/skill-queue', labelKey: 'screen.skillQueue', icon: IconTags },
      // PRE-262: the keyboard review queue. Human review is the bottleneck at
      // ~15,000 cells, and the old one-draft-per-page-load screen is what made
      // it one.
      { path: '/review-queue', labelKey: 'screen.reviewQueue', icon: IconWand },
      // PRE-261: whether the bank is still fit to measure anybody. Sits in
      // Authoring rather than next to System health — that screen is about the
      // service being up, this one is about the content being right.
      { path: '/bank-health', labelKey: 'screen.bankHealth', icon: IconActivity },
      // PRE-283: the same stages the screens above own, in the order an item
      // travels them. Last in the section because it is where you go to find
      // out WHICH of the others to open.
      { path: '/question-flow', labelKey: 'screen.questionFlow', icon: IconRoute },
      // PRE-289: the one question the screens above do not answer. Bank health
      // says whether the bank is broken and question flow says where the pipeline
      // is stuck; neither says whether we can ship. Last in the section because
      // it reads all of them.
      { path: '/launch-readiness', labelKey: 'screen.launchReadiness', icon: IconRocket },
    ],
  },
  {
    titleKey: 'app.nav.learners',
    items: [
      { path: '/users', labelKey: 'screen.users', icon: IconUsers },
      { path: '/costs', labelKey: 'screen.costs', icon: IconCoin },
    ],
  },
  {
    // The product document: what the product must do (business rules), whether
    // we actually do it (user flow / blueprint), and where the two meet (RTM).
    titleKey: 'app.nav.productDocument',
    items: [
      { path: '/business-rules', labelKey: 'screen.businessRules', icon: IconGavel },
      { path: '/user-flow', labelKey: 'screen.userFlow', icon: IconRoute },
      { path: '/traceability', labelKey: 'screen.traceability', icon: IconSitemap },
    ],
  },
  {
    titleKey: 'app.nav.platform',
    items: [
      // PRE-275: this lived under Learners, which was wrong — it reports on the
      // Worker, not on anybody's account.
      { path: '/health', labelKey: 'screen.health', icon: IconActivity },
      { path: '/config', labelKey: 'screen.config', icon: IconAdjustments },
      { path: '/simulate', labelKey: 'screen.simulate', icon: IconSparkles },
      { path: '/database', labelKey: 'screen.database', icon: IconDatabase },
      { path: '/emails', labelKey: 'screen.emails', icon: IconMail },
      { path: '/guide', labelKey: 'screen.guide', icon: IconBook },
    ],
  },
];

/** Flat view of every nav route — used to resolve the active tab and its title. */
const TABS: Tab[] = NAV_SECTIONS.flatMap((s) => s.items);

/** Short, readable model label — drops the Workers-AI vendor prefix. */
function shortModel(id: string): string {
  return id.replace(/^@cf\/[^/]+\//, '').replace(/^@cf\//, '');
}

/**
 * Header badge showing which model the generation tier runs.
 *
 * PRE-134: when no fallback is configured the badge says so in its own colour.
 * It used to render grey with the fact buried in a tooltip nobody opens, which
 * is how the deployment ran for a month on a single provider with no second
 * chance — and with JD screenshot parsing quietly unavailable — while the
 * header looked exactly like a healthy one.
 */
function ModelBadge() {
  const { data } = useLlmInfo();
  const t = useT();
  if (!data) return null;
  const { primary, fallback } = data.authoring;
  const title = fallback
    ? t('model.withFallback', { primary, fallback })
    : t('model.noFallback', { primary });
  return (
    <Tooltip label={title} multiline w={280} withArrow>
      <Badge
        variant="light"
        color={data.fallbackConfigured ? 'gray' : 'orange'}
        leftSection={
          data.fallbackConfigured ? <IconTexture size={12} /> : <IconAlertTriangle size={12} />
        }
        radius="sm"
        visibleFrom="sm"
      >
        {shortModel(primary)}
        {fallback ? ` → ${shortModel(fallback)}` : ` · ${t('model.noFallbackShort')}`}
      </Badge>
    </Tooltip>
  );
}

/** Light/dark toggle wired to Mantine's color-scheme manager. */
function ColorSchemeToggle() {
  const { setColorScheme } = useMantineColorScheme();
  const computed = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const t = useT();
  const next = computed === 'dark' ? 'light' : 'dark';
  return (
    <Tooltip
      label={t('app.colorScheme.switchTo', {
        mode: t(next === 'dark' ? 'app.colorScheme.dark' : 'app.colorScheme.light'),
      })}
      withArrow
    >
      <ActionIcon
        variant="default"
        size="lg"
        aria-label={t('app.colorScheme.toggle')}
        onClick={() => setColorScheme(next)}
      >
        {computed === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * Sign-in screen. Google OAuth plus an email/password form (the latter only
 * works when the API has EMAIL_PASSWORD_AUTH=true; identity only — admin access
 * is still the ADMIN_EMAILS allowlist). Credential login exists so the admin
 * flows can be driven/tested without an interactive OAuth round-trip.
 */
function SignIn() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signInWithEmail(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('signIn.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Group justify="center" align="center" mih="100vh" p="md">
      <Stack w={360} maw="100%" gap="sm">
        <Group justify="flex-end">
          <LanguageSwitcher />
        </Group>
        <Stack gap={2} align="center" mb="xs">
          <Title order={2}>{t('app.title')}</Title>
          <Text c="dimmed" size="sm" ta="center">
            {t('signIn.subtitle')}
          </Text>
        </Stack>
        <Button onClick={() => void signInWithGoogle()}>{t('signIn.google')}</Button>
        <Divider label={t('signIn.or')} labelPosition="center" my="xs" />
        <form onSubmit={(e) => void onSubmit(e)}>
          <Stack gap="sm">
            <TextInput
              label={t('signIn.email')}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
            <PasswordInput
              label={t('signIn.password')}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
            />
            <Button type="submit" variant="default" loading={busy} disabled={!email || !password}>
              {t('signIn.submit')}
            </Button>
          </Stack>
        </form>
        {error && (
          <Text c="red" size="sm">
            {error}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

export function App() {
  const { data: session, isPending } = useSession();
  const { pathname } = useLocation();
  const [navOpened, { toggle: toggleNav, close: closeNav }] = useDisclosure(false);
  const t = useT();

  if (isPending) {
    return (
      <Group justify="center" align="center" mih="100vh">
        <Loader />
      </Group>
    );
  }

  if (!session) return <SignIn />;

  // Active tab is derived from the URL so deep links and back/forward just work.
  // May be undefined on routes that are reachable but no longer in the nav
  // (e.g. a roadmap opened from Users) — then nothing is highlighted.
  const active = TABS.find((t) => pathname === t.path || pathname.startsWith(`${t.path}/`));
  // Heading for retained-but-unlisted deep-link routes, so they don't fall back
  // to a misleading tab title.
  const OFF_NAV_TITLES: Record<string, MessageKey> = {
    '/review': 'screen.review',
    '/content': 'screen.content',
    '/roadmaps': 'screen.roadmaps',
  };
  const offNavKey = Object.entries(OFF_NAV_TITLES).find(
    ([p]) => pathname === p || pathname.startsWith(`${p}/`),
  )?.[1];
  const heading = t(active?.labelKey ?? offNavKey ?? 'app.title');

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm', collapsed: { mobile: !navOpened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger
              opened={navOpened}
              onClick={toggleNav}
              hiddenFrom="sm"
              size="sm"
              aria-label={t('app.nav.toggle')}
            />
            <Title order={4} style={{ whiteSpace: 'nowrap' }}>
              {t('app.title')}
            </Title>
          </Group>
          <Group gap="xs" wrap="nowrap">
            <ModelBadge />
            <Text size="sm" c="dimmed" visibleFrom="md" truncate maw={200}>
              {session.user.email}
            </Text>
            <LanguageSwitcher />
            <ColorSchemeToggle />
            <Tooltip label={t('app.signOut')} withArrow>
              <ActionIcon
                variant="default"
                size="lg"
                aria-label={t('app.signOut')}
                onClick={() => void signOut()}
              >
                <IconLogout size={18} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm" style={{ overflowY: 'auto' }}>
        <Stack gap="xs">
          {NAV_SECTIONS.map((section) => (
            <Stack key={section.titleKey} gap={2}>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="xs" pt={4}>
                {t(section.titleKey)}
              </Text>
              {section.items.map((tab) => (
                <NavLink
                  key={tab.path}
                  component={Link}
                  to={tab.path}
                  active={active?.path === tab.path}
                  label={t(tab.labelKey)}
                  leftSection={<tab.icon size={18} />}
                  onClick={closeNav}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </AppShell.Navbar>

      <AppShell.Main>
        <Title order={2} mb="md">
          {heading}
        </Title>
        <Routes>
          <Route index element={<Navigate to="/generate" replace />} />
          <Route path="/generate" element={<GenerateScreen />} />
          <Route path="/questions" element={<QuestionBankScreen />} />
          <Route path="/item-models" element={<ItemModelsScreen />} />
          <Route path="/skills" element={<SkillsScreen />} />
          <Route path="/skill-queue" element={<SkillQueueScreen />} />
          <Route path="/bank-health" element={<BankHealthScreen />} />
          <Route path="/launch-readiness" element={<LaunchReadinessScreen />} />
          <Route path="/question-flow" element={<QuestionFlowScreen />} />
          <Route path="/review-queue" element={<ReviewQueueScreen />} />
          <Route path="/review" element={<ReviewScreen />} />
          <Route path="/content" element={<ContentScreen />} />
          <Route path="/users" element={<UsersScreen />} />
          <Route path="/users/:userId" element={<UsersScreen />} />
          <Route path="/roadmaps" element={<RoadmapsScreen />} />
          <Route path="/roadmaps/:roadmapId" element={<RoadmapsScreen />} />
          <Route path="/costs" element={<CostsScreen />} />
          <Route path="/health" element={<HealthScreen />} />
          <Route path="/config" element={<ConfigScreen />} />
          <Route path="/business-rules" element={<BusinessRulesScreen />} />
          <Route path="/user-flow" element={<UserFlowScreen />} />
          <Route path="/traceability" element={<TraceabilityScreen />} />
          <Route path="/simulate" element={<SimulateScreen />} />
          <Route path="/database" element={<DatabaseScreen />} />
          <Route path="/emails" element={<EmailScreen />} />
          <Route path="/guide" element={<GuideScreen />} />
          <Route path="*" element={<Navigate to="/generate" replace />} />
        </Routes>
      </AppShell.Main>
    </AppShell>
  );
}
